import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Paper,
  Divider,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import {
  Refresh,
  OpenInNew,
  Security,
  Dns,
  ListAlt,
  SwapHoriz,
  People,
  History,
  Code,
} from '@mui/icons-material';
import axios from 'axios';
import { styled } from '@mui/material/styles';
import { GlassContainer, fadeInUp } from '../styles/sharedStyles';

const GlassTableContainer = styled(TableContainer)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.02)',
  borderRadius: '20px',
  border: '1px solid rgba(255, 255, 255, 0.08)',
  backdropFilter: 'blur(10px)',
  '& .MuiTableCell-root': {
    borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
    padding: '14px 16px',
  },
  '& .MuiTableHead-root .MuiTableCell-root': {
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
    color: 'rgba(255, 255, 255, 0.6)',
    fontWeight: 600,
    fontSize: '0.8rem',
    textTransform: 'uppercase',
    letterSpacing: '0.8px',
  },
  '& .MuiTableBody-root .MuiTableRow-root:hover': {
    backgroundColor: 'rgba(0, 212, 255, 0.04)',
  },
}));

const DEFAULT_NPM_BASE_URL = 'http://192.168.0.100:81';

const NginxProxyManager = ({ apiBaseUrl, token }) => {
  const [subTab, setSubTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [proxyHosts, setProxyHosts] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [accessLists, setAccessLists] = useState([]);
  const [redirections, setRedirections] = useState([]);
  const [streams, setStreams] = useState([]);
  const [npmUsers, setNpmUsers] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [selectedHost, setSelectedHost] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const fetchAll = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const [hostsRes, certsRes, accessRes, redirsRes, streamsRes, usersRes, auditRes] =
        await Promise.all([
          axios.get(`${apiBaseUrl}/api/nginx/proxy-hosts`, {
            headers: { Authorization: `Bearer ${token}` },
            timeout: 15000,
          }),
          axios.get(`${apiBaseUrl}/api/nginx/certificates`, {
            headers: { Authorization: `Bearer ${token}` },
            timeout: 15000,
          }),
          axios.get(`${apiBaseUrl}/api/nginx/access-lists`, {
            headers: { Authorization: `Bearer ${token}` },
            timeout: 15000,
          }),
          axios.get(`${apiBaseUrl}/api/nginx/redirection-hosts`, {
            headers: { Authorization: `Bearer ${token}` },
            timeout: 15000,
          }),
          axios.get(`${apiBaseUrl}/api/nginx/streams`, {
            headers: { Authorization: `Bearer ${token}` },
            timeout: 15000,
          }),
          axios.get(`${apiBaseUrl}/api/nginx/users`, {
            headers: { Authorization: `Bearer ${token}` },
            timeout: 15000,
          }),
          axios.get(`${apiBaseUrl}/api/nginx/audit-log`, {
            headers: { Authorization: `Bearer ${token}` },
            timeout: 15000,
          }),
        ]);

      setProxyHosts(hostsRes.data?.hosts || []);
      setCertificates(certsRes.data?.certificates || []);
      setAccessLists(accessRes.data?.access_lists || []);
      setRedirections(redirsRes.data?.redirections || []);
      setStreams(streamsRes.data?.streams || []);
      setNpmUsers(usersRes.data?.users || []);
      setAuditLog(auditRes.data?.audit || []);

      const firstError = [hostsRes, certsRes, accessRes, redirsRes, streamsRes, usersRes, auditRes]
        .find(r => r.data?.error)
        ?.data?.error;
      if (firstError) {
        setError(firstError);
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to load NPM data');
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, token]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const getAccessIcon = (name) => {
    const lower = (name || '').toLowerCase();
    if (!lower || lower === 'public') return '/public.png';
    if (lower.includes('vpn')) return '/vpn.png';
    return null;
  };

  const accessListMap = useMemo(() => {
    const map = {};
    (accessLists || []).forEach(al => {
      if (al.id != null) map[al.id] = al.name;
    });
    return map;
  }, [accessLists]);

  const renderEmpty = (message) => (
    <Box sx={{ textAlign: 'center', py: 6, color: 'rgba(255,255,255,0.4)' }}>
      <Typography>{message}</Typography>
    </Box>
  );

  const renderProxyHosts = () => {
    if (proxyHosts.length === 0) return renderEmpty('No proxy hosts configured');
    return (
      <GlassTableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Domain</TableCell>
              <TableCell>Forward</TableCell>
              <TableCell>Access</TableCell>
              <TableCell>SSL</TableCell>
              <TableCell>WebSockets</TableCell>
              <TableCell>Block Exploits</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {proxyHosts.map((host) => {
              const accessName = host.access_list?.name || (host.access_list_id ? accessListMap[host.access_list_id] : null);
              const accessLabel = accessName || 'Public';
              const accessColor = accessName ? '#ffab40' : 'rgba(255,255,255,0.5)';
              const accessBg = accessName ? 'rgba(255, 171, 64, 0.15)' : 'rgba(255, 255, 255, 0.05)';
              const accessBorder = accessName ? 'rgba(255, 171, 64, 0.3)' : 'rgba(255,255,255,0.1)';
              const accessIcon = getAccessIcon(accessName);
              return (
                <TableRow
                  key={host.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => { setSelectedHost(host); setDetailOpen(true); }}
                >
                  <TableCell sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.8rem' }}>
                    {host.id}
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ fontWeight: 600, color: '#fff' }}>
                      {(host.domain_names || []).join(', ')}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.8rem' }}>
                    {host.forward_scheme}://{host.forward_host}:{host.forward_port}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {accessIcon && (
                        <Box
                          component="img"
                          src={accessIcon}
                          alt={accessLabel}
                          sx={{ width: 18, height: 18, objectFit: 'contain' }}
                        />
                      )}
                      <Chip
                        label={accessLabel}
                        size="small"
                        sx={{
                          background: accessBg,
                          color: accessColor,
                          border: `1px solid ${accessBorder}`,
                        }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={host.ssl_forced ? 'Forced' : (host.certificate_id ? 'Yes' : 'No')}
                      size="small"
                      sx={{
                        background: host.ssl_forced
                          ? 'rgba(0, 212, 255, 0.15)'
                          : 'rgba(255, 255, 255, 0.05)',
                        color: host.ssl_forced ? '#00d4ff' : 'rgba(255,255,255,0.5)',
                        border: `1px solid ${host.ssl_forced ? 'rgba(0, 212, 255, 0.3)' : 'rgba(255,255,255,0.1)'}`,
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={host.allow_websocket_upgrade ? 'Yes' : 'No'}
                      size="small"
                      sx={{
                        background: host.allow_websocket_upgrade
                          ? 'rgba(0, 255, 136, 0.15)'
                          : 'rgba(255, 255, 255, 0.05)',
                        color: host.allow_websocket_upgrade ? '#00ff88' : 'rgba(255,255,255,0.5)',
                        border: `1px solid ${host.allow_websocket_upgrade ? 'rgba(0, 255, 136, 0.3)' : 'rgba(255,255,255,0.1)'}`,
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={host.block_exploits ? 'Yes' : 'No'}
                      size="small"
                      sx={{
                        background: host.block_exploits
                          ? 'rgba(255, 171, 64, 0.15)'
                          : 'rgba(255, 255, 255, 0.05)',
                        color: host.block_exploits ? '#ffab40' : 'rgba(255,255,255,0.5)',
                        border: `1px solid ${host.block_exploits ? 'rgba(255, 171, 64, 0.3)' : 'rgba(255,255,255,0.1)'}`,
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={host.enabled ? 'Enabled' : 'Disabled'}
                      size="small"
                      sx={{
                        background: host.enabled
                          ? 'rgba(0, 255, 136, 0.15)'
                          : 'rgba(255, 68, 68, 0.15)',
                        color: host.enabled ? '#00ff88' : '#ff6b6b',
                        border: `1px solid ${host.enabled ? 'rgba(0, 255, 136, 0.3)' : 'rgba(255, 68, 68, 0.3)'}`,
                      }}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </GlassTableContainer>
    );
  };

  const renderCertificates = () => {
    if (certificates.length === 0) return renderEmpty('No certificates found');
    return (
      <GlassTableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Nice Name</TableCell>
              <TableCell>Provider</TableCell>
              <TableCell>Domains</TableCell>
              <TableCell>Expires</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {certificates.map((cert) => (
              <TableRow key={cert.id} hover>
                <TableCell sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.8rem' }}>
                  {cert.id}
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#fff' }}>{cert.nice_name}</TableCell>
                <TableCell>
                  <Chip
                    label={cert.provider || 'N/A'}
                    size="small"
                    sx={{
                      background: 'rgba(179, 136, 255, 0.15)',
                      color: '#b388ff',
                      border: '1px solid rgba(179, 136, 255, 0.3)',
                    }}
                  />
                </TableCell>
                <TableCell sx={{ fontSize: '0.85rem' }}>
                  {(cert.domain_names || []).join(', ') || '—'}
                </TableCell>
                <TableCell sx={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.7)' }}>
                  {cert.expires_on || '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </GlassTableContainer>
    );
  };

  const renderAccessLists = () => {
    if (accessLists.length === 0) return renderEmpty('No access lists configured');
    return (
      <GlassTableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Satisfy</TableCell>
              <TableCell>Rules</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {accessLists.map((al) => (
              <TableRow key={al.id} hover>
                <TableCell sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.8rem' }}>
                  {al.id}
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#fff' }}>{al.name}</TableCell>
                <TableCell>
                  <Chip
                    label={al.satisfy_any ? 'Any' : 'All'}
                    size="small"
                    sx={{
                      background: al.satisfy_any
                        ? 'rgba(255, 171, 64, 0.15)'
                        : 'rgba(0, 212, 255, 0.15)',
                      color: al.satisfy_any ? '#ffab40' : '#00d4ff',
                      border: `1px solid ${al.satisfy_any ? 'rgba(255, 171, 64, 0.3)' : 'rgba(0, 212, 255, 0.3)'}`,
                    }}
                  />
                </TableCell>
                <TableCell sx={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.7)' }}>
                  {(al.items || []).length}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </GlassTableContainer>
    );
  };

  const renderRedirections = () => {
    if (redirections.length === 0) return renderEmpty('No redirection hosts configured');
    return (
      <GlassTableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Domain</TableCell>
              <TableCell>HTTP Code</TableCell>
              <TableCell>Forward To</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {redirections.map((r) => (
              <TableRow key={r.id} hover>
                <TableCell sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.8rem' }}>
                  {r.id}
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#fff' }}>
                  {(r.domain_names || []).join(', ')}
                </TableCell>
                <TableCell sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.85rem' }}>
                  {r.forward_http_code}
                </TableCell>
                <TableCell sx={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.7)' }}>
                  {r.forward_domain_name || '—'}
                </TableCell>
                <TableCell>
                  <Chip
                    label={r.enabled ? 'Enabled' : 'Disabled'}
                    size="small"
                    sx={{
                      background: r.enabled
                        ? 'rgba(0, 255, 136, 0.15)'
                        : 'rgba(255, 68, 68, 0.15)',
                      color: r.enabled ? '#00ff88' : '#ff6b6b',
                      border: `1px solid ${r.enabled ? 'rgba(0, 255, 136, 0.3)' : 'rgba(255, 68, 68, 0.3)'}`,
                    }}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </GlassTableContainer>
    );
  };

  const renderStreams = () => {
    if (streams.length === 0) return renderEmpty('No TCP/UDP streams configured');
    return (
      <GlassTableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Incoming Port</TableCell>
              <TableCell>Forward Host</TableCell>
              <TableCell>Forward Port</TableCell>
              <TableCell>TCP</TableCell>
              <TableCell>UDP</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {streams.map((s) => (
              <TableRow key={s.id} hover>
                <TableCell sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.8rem' }}>
                  {s.id}
                </TableCell>
                <TableCell sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.85rem', color: '#00d4ff' }}>
                  {s.incoming_port}
                </TableCell>
                <TableCell sx={{ fontSize: '0.85rem' }}>{s.forwarding_host}</TableCell>
                <TableCell sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.85rem' }}>
                  {s.forwarding_port}
                </TableCell>
                <TableCell>
                  <Chip
                    label={s.tcp_forwarding ? 'Yes' : 'No'}
                    size="small"
                    sx={{
                      background: s.tcp_forwarding
                        ? 'rgba(0, 255, 136, 0.15)'
                        : 'rgba(255, 255, 255, 0.05)',
                      color: s.tcp_forwarding ? '#00ff88' : 'rgba(255,255,255,0.5)',
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={s.udp_forwarding ? 'Yes' : 'No'}
                    size="small"
                    sx={{
                      background: s.udp_forwarding
                        ? 'rgba(0, 255, 136, 0.15)'
                        : 'rgba(255, 255, 255, 0.05)',
                      color: s.udp_forwarding ? '#00ff88' : 'rgba(255,255,255,0.5)',
                    }}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </GlassTableContainer>
    );
  };

  const renderUsers = () => {
    if (npmUsers.length === 0) return renderEmpty('No NPM users found');
    return (
      <GlassTableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Roles</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {npmUsers.map((u) => (
              <TableRow key={u.id} hover>
                <TableCell sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.8rem' }}>
                  {u.id}
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#fff' }}>{u.name}</TableCell>
                <TableCell sx={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.7)' }}>
                  {u.email || '—'}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {(u.roles || []).map((role, idx) => (
                      <Chip
                        key={idx}
                        label={role}
                        size="small"
                        sx={{
                          background: 'rgba(0, 212, 255, 0.1)',
                          color: '#00d4ff',
                          border: '1px solid rgba(0, 212, 255, 0.2)',
                        }}
                      />
                    ))}
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    label={u.is_disabled ? 'Disabled' : 'Active'}
                    size="small"
                    sx={{
                      background: u.is_disabled
                        ? 'rgba(255, 68, 68, 0.15)'
                        : 'rgba(0, 255, 136, 0.15)',
                      color: u.is_disabled ? '#ff6b6b' : '#00ff88',
                      border: `1px solid ${u.is_disabled ? 'rgba(255, 68, 68, 0.3)' : 'rgba(0, 255, 136, 0.3)'}`,
                    }}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </GlassTableContainer>
    );
  };

  const renderAuditLog = () => {
    if (auditLog.length === 0) return renderEmpty('No audit log entries');
    return (
      <GlassTableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Timestamp</TableCell>
              <TableCell>Action</TableCell>
              <TableCell>User ID</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {auditLog.map((entry) => (
              <TableRow key={entry.id} hover>
                <TableCell sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.8rem' }}>
                  {entry.id}
                </TableCell>
                <TableCell sx={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.7)' }}>
                  {entry.created_on || '—'}
                </TableCell>
                <TableCell sx={{ fontSize: '0.85rem', color: '#fff', fontWeight: 500 }}>
                  {entry.action}
                </TableCell>
                <TableCell sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.8rem' }}>
                  {entry.user_id ?? '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </GlassTableContainer>
    );
  };

  return (
    <Box sx={{ animation: `${fadeInUp} 0.5s ease-out` }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '16px',
              background: 'linear-gradient(135deg, rgba(0, 212, 255, 0.15), rgba(179, 136, 255, 0.15))',
              border: '1px solid rgba(0, 212, 255, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
              overflow: 'hidden',
            }}
          >
            <Box
              component="img"
              src="/nginx-proxy-manager.png"
              alt="Nginx Proxy Manager"
              sx={{ width: 36, height: 36, objectFit: 'contain' }}
            />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700, letterSpacing: '0.5px' }}>
              Nginx Proxy Manager
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Dns sx={{ fontSize: 14 }} /> {DEFAULT_NPM_BASE_URL}
            </Typography>
          </Box>
        </Box>
        <Tooltip title="Refresh data">
          <IconButton
            onClick={fetchAll}
            disabled={loading}
            sx={{
              color: '#00d4ff',
              background: 'rgba(0, 212, 255, 0.1)',
              border: '1px solid rgba(0, 212, 255, 0.1)',
              '&:hover': { background: 'rgba(0, 212, 255, 0.2)', borderColor: 'rgba(0, 212, 255, 0.3)' },
              '&.Mui-disabled': { background: 'rgba(0, 212, 255, 0.05)' },
            }}
          >
            <Refresh sx={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 3,
            borderRadius: '12px',
            background: 'rgba(211, 47, 47, 0.1)',
            color: '#ff8a80',
            border: '1px solid rgba(211, 47, 47, 0.2)',
            '& .MuiAlert-icon': { color: '#ff8a80' },
          }}
        >
          {error}
        </Alert>
      )}

      <Box
        sx={{
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          background: 'rgba(255, 255, 255, 0.02)',
          px: 0,
          overflowX: { xs: 'auto', sm: 'unset' },
          borderRadius: '16px 16px 0 0',
        }}
      >
        <Tabs
          value={subTab}
          onChange={(e, newValue) => setSubTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: { xs: '48px', sm: '56px' },
            '& .MuiTabs-indicator': {
              background: (theme) => theme.palette.primary.main,
              height: '3px',
            },
            '& .MuiTab-root': {
              color: 'rgba(255, 255, 255, 0.6)',
              textTransform: 'none',
              fontSize: { xs: '0.75rem', sm: '0.9rem' },
              fontWeight: 500,
              py: { xs: 1, sm: 1.5 },
              px: { xs: 1.5, sm: 2.5 },
              minWidth: { xs: 'auto', sm: '100px' },
              transition: 'all 0.3s ease',
              '&:hover': {
                color: '#ffffff',
                background: 'rgba(255, 255, 255, 0.05)',
              },
              '&.Mui-selected': {
                color: (theme) => theme.palette.primary.main,
                background: (theme) => alpha(theme.palette.primary.main, 0.05),
              },
            },
          }}
        >
          <Tab icon={<Dns sx={{ fontSize: 18 }} />} iconPosition="start" label="Proxy Hosts" />
          <Tab icon={<Security sx={{ fontSize: 18 }} />} iconPosition="start" label="Certificates" />
          <Tab icon={<ListAlt sx={{ fontSize: 18 }} />} iconPosition="start" label="Access Lists" />
          <Tab icon={<SwapHoriz sx={{ fontSize: 18 }} />} iconPosition="start" label="Redirections" />
          <Tab icon={<SwapHoriz sx={{ fontSize: 18 }} />} iconPosition="start" label="Streams" />
          <Tab icon={<People sx={{ fontSize: 18 }} />} iconPosition="start" label="Users" />
          <Tab icon={<History sx={{ fontSize: 18 }} />} iconPosition="start" label="Audit Log" />
        </Tabs>
      </Box>

      <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
        {loading && proxyHosts.length === 0 && certificates.length === 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 10, gap: 2 }}>
            <CircularProgress size={40} thickness={4} sx={{ color: '#00d4ff' }} />
            <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.9rem' }}>
              Loading Nginx Proxy Manager data...
            </Typography>
          </Box>
        ) : (
          <Box sx={{ animation: `${fadeInUp} 0.4s ease-out` }}>
            {subTab === 0 && renderProxyHosts()}
            {subTab === 1 && renderCertificates()}
            {subTab === 2 && renderAccessLists()}
            {subTab === 3 && renderRedirections()}
            {subTab === 4 && renderStreams()}
            {subTab === 5 && renderUsers()}
            {subTab === 6 && renderAuditLog()}
          </Box>
        )}
      </Box>

      <Dialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            background: '#1a1a1a',
            backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.05))',
            color: '#fff',
            borderRadius: 3,
            border: '1px solid rgba(255,255,255,0.1)',
          }
        }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid rgba(255,255,255,0.1)', pb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Code />
            Proxy Host Details
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {selectedHost && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Box>
                <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.5)', mb: 1, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Domains
                </Typography>
                <Typography sx={{ color: '#fff', fontWeight: 600 }}>
                  {(selectedHost.domain_names || []).join(', ')}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.5)', mb: 1, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Forward
                </Typography>
                <Typography sx={{ fontFamily: 'JetBrains Mono, monospace', color: '#00d4ff' }}>
                  {selectedHost.forward_scheme}://{selectedHost.forward_host}:{selectedHost.forward_port}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.5)', mb: 1, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Access
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {(selectedHost.access_list?.name || selectedHost.access_list_id) && (
                    <Box
                      component="img"
                      src={getAccessIcon(selectedHost.access_list?.name || accessListMap[selectedHost.access_list_id])}
                      alt=""
                      sx={{ width: 18, height: 18, objectFit: 'contain' }}
                    />
                  )}
                  <Chip
                    label={selectedHost.access_list?.name || (selectedHost.access_list_id ? accessListMap[selectedHost.access_list_id] : 'Public')}
                    size="small"
                    sx={{
                      background: (selectedHost.access_list?.name || selectedHost.access_list_id) ? 'rgba(255, 171, 64, 0.15)' : 'rgba(255, 255, 255, 0.05)',
                      color: (selectedHost.access_list?.name || selectedHost.access_list_id) ? '#ffab40' : 'rgba(255,255,255,0.5)',
                      border: `1px solid ${(selectedHost.access_list?.name || selectedHost.access_list_id) ? 'rgba(255, 171, 64, 0.3)' : 'rgba(255,255,255,0.1)'}`,
                    }}
                  />
                </Box>
              </Box>

              {(selectedHost.locations || []).length > 0 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.5)', mb: 1, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Custom Locations
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {selectedHost.locations.map((loc, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          p: 1.5,
                          borderRadius: '12px',
                          background: 'rgba(255, 255, 255, 0.02)',
                          border: '1px solid rgba(255, 255, 255, 0.06)',
                        }}
                      >
                        <Typography sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.85rem', color: '#fff' }}>
                          {loc.path}
                        </Typography>
                        <Typography sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.8rem', color: 'rgba(255,255,255,0.6)', mt: 0.5 }}>
                          {'->'} {loc.forward_scheme}://{loc.forward_host}:{loc.forward_port}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              {selectedHost.advanced_config && (
                <Box>
                  <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.5)', mb: 1, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Advanced Config
                  </Typography>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: '12px',
                      background: 'rgba(0,0,0,0.3)',
                      border: '1px solid rgba(255,255,255,0.08)',
                      fontFamily: 'JetBrains Mono, Consolas, monospace',
                      fontSize: '0.8rem',
                      lineHeight: 1.6,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-all',
                      color: 'rgba(255,255,255,0.85)',
                    }}
                  >
                    {selectedHost.advanced_config}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
          <Button onClick={() => setDetailOpen(false)} sx={{ color: 'rgba(255,255,255,0.6)' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NginxProxyManager;
